import { Userbot, UserbotCatalog } from "../../interfaces"
import { isValidJson, parseJsonValue, isBlank } from "../common/jsonUtils"
import { coerceFieldValue } from "../fields/fieldSpecCoerce"
import { validateConditionExpr } from "../fields/conditionExpr"
import { paramsToValues, valuesToParams } from "./actionParams"
import {
  serializeHistoryConsent,
  validateHistoryConsent,
} from "@/core/utils/historyConsent"
import {
  LaunchTargetState,
  launchTargetFromDto,
  serializeLaunchTarget,
  validateLaunchTarget,
} from "../common/launchTarget"

export type ChainExecutor = "client" | "role"

/** Каталог действий в виде map по имени. */
export type Descriptors = Record<string, UserbotCatalog.ActionDescriptor>

/* ----------------------- Form state (рекурсивное дерево) ----------------------- */

export interface BaseNodeForm {
  extraDto?: Record<string, unknown>
  key: string
  id: string
  onError?: Userbot.StepOnError
  retryAttempts?: string
  retryBackoffMs?: string
  timeoutMs?: string
  saves: { action: Userbot.SaveSpec["action"]; paramsJson: string }[]
}

export interface ActionNodeForm extends BaseNodeForm {
  kind: "action"
  action: string
  paramsValues: Record<string, unknown>
  rawParamsJson?: string
}

export interface BranchNodeForm extends BaseNodeForm {
  kind: "branch"
  cases: {
    key: string
    extraDto?: Record<string, unknown>
    when: Userbot.ConditionExpr | null
    then: NodeFormState[]
  }[]
  elseSteps?: NodeFormState[]
}

export interface ForEachNodeForm extends BaseNodeForm {
  kind: "forEach"
  items: unknown
  itemVar?: string
  maxIterations?: string
  body: NodeFormState[]
}

export interface CallChainNodeForm extends BaseNodeForm {
  kind: "callChain"
  templateId?: string
  clientId?: string // пустая строка = in-place; иначе uuid клиента или whole-string {{...}}
  name?: string // пустая строка = авто-имя бэка
  inlineSteps?: NodeFormState[]
  inlineExtraDto?: Record<string, unknown>
  inputJson?: string
}

export interface WaitNodeForm extends BaseNodeForm {
  kind: "wait"
  durationMs?: string
  until?: string
}

export interface FlowOnUpdateNodeForm extends BaseNodeForm {
  kind: "flow.onUpdate"
  match: Userbot.ConditionExpr | null // null = match-all (A3)
  maxFires?: string // "" = без лимита; число-строка или whole-string {{...}}
  steps?: NodeFormState[]
}

export type NodeFormState =
  | ActionNodeForm
  | BranchNodeForm
  | ForEachNodeForm
  | CallChainNodeForm
  | WaitNodeForm
  | FlowOnUpdateNodeForm

/** Backward compat alias (will be removed in Phase N). */
export type StepFormState = ActionNodeForm

export interface ChainFormState extends LaunchTargetState {
  extraDto?: Record<string, unknown>
  name: string
  comment?: string
  contextJson?: string
  scheduleDate?: string
  steps: NodeFormState[]
}

/* ----------------------- Утилиты ----------------------- */

const RESERVED_IDS = new Set(["ctx", "item", "index", "input"])

/** Имя DTO-поля тела flow.onUpdate (A2 — единственный источник). Сверить со слотом
 *  NodeDescriptor.slots из runtime-каталога GET /userbot/catalog/node-kinds;
 *  расхождение имени → правка только этой константы и FlowOnUpdateNode.steps. */
export const FLOW_ON_UPDATE_BODY_KEY = "steps" as const

/** Whole-string {{...}}-плейсхолдер (тот же паттерн, что fieldSpecInput.tsx:45). */
export const isWholeStringTemplate = (v: string): boolean =>
  /^\{\{[\s\S]+\}\}$/.test(v)

const asString = (v: unknown): string | undefined =>
  v === undefined || v === null ? undefined : String(v)

const isEmptyValue = (spec: UserbotCatalog.FieldSpec, raw: unknown): boolean =>
  spec.type === "array"
    ? !Array.isArray(raw) || raw.length === 0
    : isBlank(asString(raw))

/** Составной ctx-ключ для отладки resume (TZ §8.4). */
export const composeNodeKey = (path: string, id: string): string => path + id

let keyCounter = 0
const nextKey = () => `k-${Date.now()}-${keyCounter++}`

/* ----------------------- Сериализация form → DTO ----------------------- */

const hasTypedParams = (
  action: string,
  descriptors: Descriptors,
): UserbotCatalog.ActionDescriptor | null => {
  const descriptor = descriptors[action]
  return descriptor && descriptor.params.length ? descriptor : null
}

/** Строит params action-узла из FieldSpec или сырого JSON. */
const collectStepParams = (
  step: ActionNodeForm,
  descriptors: Descriptors,
): Record<string, unknown> => {
  if (!isBlank(step.rawParamsJson)) {
    return parseJsonValue<Record<string, unknown>>(step.rawParamsJson) ?? {}
  }
  const descriptor = hasTypedParams(step.action, descriptors)
  if (descriptor) {
    return valuesToParams(descriptor.params, step.paramsValues)
  }

  const parsed = parseJsonValue<Record<string, unknown>>(step.rawParamsJson)
  return parsed ?? {}
}

export const buildStepParams = (
  step: ActionNodeForm,
  descriptors: Descriptors,
): Record<string, unknown> => {
  const params = collectStepParams(step, descriptors)
  if (step.action !== "telegram.exportHistory") return params
  return {
    ...params,
    ...serializeHistoryConsent(params.downloadMessages, params.analyze),
  }
}

/** Базовые поля узла (id, onError, retry, timeoutMs, save). */
const buildBaseFields = (node: BaseNodeForm): Userbot.BaseNode => {
  const base: Userbot.BaseNode = { ...node.extraDto, id: node.id }

  if (node.onError && (node.onError !== "fail" || base.onError !== undefined))
    base.onError = node.onError

  const attempts = node.retryAttempts ? Number(node.retryAttempts) : undefined
  const backoffMs = node.retryBackoffMs
    ? Number(node.retryBackoffMs)
    : undefined
  if (attempts || backoffMs) {
    base.retry = {}
    if (attempts) base.retry.attempts = attempts
    if (backoffMs) base.retry.backoffMs = backoffMs
  }

  if (node.timeoutMs) base.timeoutMs = Number(node.timeoutMs)

  const saves = node.saves
    .filter((s) => !isBlank(s.paramsJson))
    .map((s) => ({
      action: s.action,
      params: (parseJsonValue(s.paramsJson) as Record<string, unknown>) ?? {},
    }))
  if (saves.length) base.save = saves

  return base
}

/** Рекурсивно строит ChainNode из NodeFormState. */
const buildNode = (
  node: NodeFormState,
  descriptors: Descriptors,
): Userbot.ChainNode => {
  const base = buildBaseFields(node)

  switch (node.kind) {
    case "action":
      return {
        ...base,
        kind: "action",
        action: node.action,
        params: buildStepParams(node, descriptors),
      } as Userbot.ActionNode

    case "branch":
      return {
        ...base,
        kind: "branch",
        cases: node.cases.map((c) => ({
          ...c.extraDto,
          when: c.when ?? {
            op: "eq",
            left: true,
            right: true,
          },
          then: c.then.map((n) => buildNode(n, descriptors)),
        })),
        else: node.elseSteps?.length
          ? node.elseSteps.map((n) => buildNode(n, descriptors))
          : undefined,
      } as Userbot.BranchNode

    case "forEach": {
      const fe: Userbot.ForEachNode = {
        ...base,
        kind: "forEach",
        items: node.items as unknown[] | string,
        body: node.body.map((n) => buildNode(n, descriptors)),
      }
      if (node.itemVar) fe.itemVar = node.itemVar
      if (node.maxIterations) fe.maxIterations = Number(node.maxIterations)
      // mode всегда sequential — не сериализуем (default)
      return fe
    }

    case "callChain": {
      const cc: Userbot.CallChainNode = {
        ...base,
        kind: "callChain",
      }
      if (node.templateId) cc.templateId = node.templateId
      if (node.inlineSteps?.length)
        cc.inline = {
          ...node.inlineExtraDto,
          steps: node.inlineSteps.map((n) => buildNode(n, descriptors)),
        }
      const input = parseJsonValue<Record<string, unknown>>(node.inputJson)
      if (input) cc.input = input
      if (!isBlank(node.clientId)) cc.clientId = node.clientId
      if (!isBlank(node.name)) cc.name = node.name
      return cc
    }

    case "wait": {
      const w: Userbot.WaitNode = { ...base, kind: "wait" }
      if (node.durationMs)
        w.durationMs = isWholeStringTemplate(node.durationMs)
          ? node.durationMs
          : Number(node.durationMs)
      if (node.until) w.until = node.until
      return w
    }

    case "flow.onUpdate": {
      const fo: Userbot.FlowOnUpdateNode = {
        ...base,
        kind: "flow.onUpdate",
      }
      if (node.match !== null) fo.match = node.match
      const maxFires = node.maxFires ?? ""
      if (!isBlank(maxFires)) {
        fo.maxFires = isWholeStringTemplate(maxFires)
          ? maxFires
          : Number(maxFires)
      }
      if (node.steps?.length)
        fo[FLOW_ON_UPDATE_BODY_KEY] = node.steps.map((n) =>
          buildNode(n, descriptors),
        )
      return fo
    }
  }
}

/** Валидация формы конструктора. Возвращает массив сообщений об ошибках. */
export const validateChain = (
  state: ChainFormState,
  descriptors: Descriptors,
  limits?: UserbotCatalog.CatalogLimits,
): string[] => {
  const errors: string[] = []
  const maxNodeDepth = limits?.maxNodeDepth ?? Infinity
  const maxForEach = limits?.maxForEach ?? Infinity

  if (isBlank(state.name)) errors.push("Укажите название цепочки")

  errors.push(...validateLaunchTarget(state))

  if (!state.steps.length) {
    errors.push("Добавьте хотя бы один шаг")
  }

  if (!isBlank(state.contextJson) && !isValidJson(state.contextJson)) {
    errors.push("Контекст — некорректный JSON")
  }

  // Рекурсивная валидация узлов
  const allIds = new Set<string>()
  const validateNode = (
    node: NodeFormState,
    scopeIds: Set<string>,
    depth: number,
  ) => {
    if (depth > maxNodeDepth) {
      errors.push(
        `Узел ${node.id}: превышена максимальная глубина ${maxNodeDepth}`,
      )
      return
    }

    if (isBlank(node.id)) {
      errors.push(`Узел: укажите id`)
    } else if (RESERVED_IDS.has(node.id)) {
      errors.push(`Узел: id "${node.id}" — RESERVED`)
    } else if (scopeIds.has(node.id)) {
      errors.push(`Узел: id "${node.id}" не уникален в scope`)
    } else {
      scopeIds.add(node.id)
    }

    // JSON-поля
    node.saves.forEach((s, sIdx) => {
      if (!isBlank(s.paramsJson) && !isValidJson(s.paramsJson)) {
        errors.push(`Узел ${node.id}: save[${sIdx}] — некорректный JSON`)
      }
    })

    switch (node.kind) {
      case "action": {
        if (isBlank(node.action)) {
          errors.push(`Узел ${node.id}: выберите действие`)
        }
        const descriptor = hasTypedParams(node.action, descriptors)
        // Validate coercion at this boundary, including nested object/array/union
        // JSON fields. Serialization must never be the first draft parser.
        const typedParams: Record<string, unknown> = {}
        if (!isBlank(node.rawParamsJson)) {
          if (
            !isValidJson(node.rawParamsJson) ||
            !parseJsonValue(node.rawParamsJson) ||
            typeof parseJsonValue(node.rawParamsJson) !== "object" ||
            Array.isArray(parseJsonValue(node.rawParamsJson))
          )
            errors.push(
              `Узел ${node.id}: параметры (JSON) — некорректный JSON объекта`,
            )
        } else if (descriptor) {
          for (const spec of descriptor.params) {
            const raw = node.paramsValues[spec.key]
            const empty = isEmptyValue(spec, raw)
            if (spec.required && empty) {
              errors.push(
                `Узел ${node.id}: параметр "${spec.label}" обязателен`,
              )
            }
            if (!empty) {
              try {
                const value = coerceFieldValue(spec, raw)
                if (value !== undefined) typedParams[spec.key] = value
              } catch (error) {
                errors.push(
                  `Узел ${node.id}: параметр "${spec.label}" — ${error instanceof SyntaxError ? "некорректный JSON" : "некорректное значение"}`,
                )
              }
            }
          }
        } else if (
          !isBlank(node.rawParamsJson) &&
          !isValidJson(node.rawParamsJson)
        ) {
          errors.push(`Узел ${node.id}: параметры (JSON) — некорректный JSON`)
        }
        if (node.action === "telegram.exportHistory") {
          if (
            !isBlank(node.rawParamsJson) &&
            !isValidJson(node.rawParamsJson)
          ) {
            errors.push(`Узел ${node.id}: параметры (JSON) — некорректный JSON`)
          } else {
            const params = !isBlank(node.rawParamsJson)
              ? (parseJsonValue<Record<string, unknown>>(node.rawParamsJson) ??
                {})
              : typedParams
            errors.push(
              ...validateHistoryConsent(
                params.downloadMessages,
                params.analyze,
              ).map((error) => `Узел ${node.id}: ${error}`),
            )
          }
        }
        break
      }

      case "branch": {
        if (!node.cases.length) {
          errors.push(`Узел ${node.id}: добавьте хотя бы один case`)
        }
        node.cases.forEach((c, i) => {
          if (!c.then.length) {
            errors.push(`Узел ${node.id}: case ${i + 1} — добавьте узел в then`)
          }
          if (c.when) {
            const condErrs = validateConditionExpr(c.when, limits)
            condErrs.forEach((e) =>
              errors.push(`Узел ${node.id}: case ${i + 1} condition: ${e}`),
            )
          }
          c.then.forEach((n) => validateNode(n, scopeIds, depth + 1))
        })
        node.elseSteps?.forEach((n) => validateNode(n, scopeIds, depth + 1))
        break
      }

      case "forEach": {
        if (node.itemVar && RESERVED_IDS.has(node.itemVar)) {
          errors.push(`Узел ${node.id}: itemVar "${node.itemVar}" — RESERVED`)
        }
        if (node.maxIterations) {
          const max = Number(node.maxIterations)
          if (max > maxForEach) {
            errors.push(
              `Узел ${node.id}: maxIterations ${max} > лимита ${maxForEach}`,
            )
          }
        }
        node.body.forEach((n) => validateNode(n, scopeIds, depth + 1))
        break
      }

      case "callChain": {
        const hasTemplate = !isBlank(node.templateId)
        const hasInline = !!node.inlineSteps?.length
        if (!hasTemplate && !hasInline) {
          errors.push(
            `Узел ${node.id}: callChain требует templateId или inline (XOR)`,
          )
        }
        if (hasTemplate && hasInline) {
          errors.push(
            `Узел ${node.id}: callChain — только одно из templateId или inline`,
          )
        }
        if (!isBlank(node.name) && isBlank(node.clientId)) {
          errors.push(
            `Узел ${node.id}: name используется только при спавне (задайте clientId)`,
          )
        }
        if (!isBlank(node.inputJson) && !isValidJson(node.inputJson)) {
          errors.push(`Узел ${node.id}: input — некорректный JSON`)
        }
        node.inlineSteps?.forEach((n) => validateNode(n, scopeIds, depth + 1))
        break
      }

      case "wait": {
        const hasDuration = !isBlank(node.durationMs)
        const hasUntil = !isBlank(node.until)
        if (!hasDuration && !hasUntil) {
          errors.push(
            `Узел ${node.id}: wait требует durationMs или until (XOR)`,
          )
        }
        if (hasDuration && hasUntil) {
          errors.push(
            `Узел ${node.id}: wait — только одно из durationMs или until`,
          )
        }
        if (
          hasDuration &&
          !isWholeStringTemplate(node.durationMs!) &&
          (!Number.isFinite(Number(node.durationMs)) ||
            Number(node.durationMs) < 0)
        )
          errors.push(
            `Узел ${node.id}: durationMs — число мс или whole-string {{...}}`,
          )
        break
      }

      case "flow.onUpdate": {
        if (node.match !== null) {
          const condErrs = validateConditionExpr(node.match, limits)
          condErrs.forEach((e) =>
            errors.push(`Узел ${node.id}: match condition: ${e}`),
          )
        }
        const maxFires = node.maxFires ?? ""
        if (!isBlank(maxFires)) {
          const n = Number(maxFires)
          const valid =
            isWholeStringTemplate(maxFires) || (Number.isInteger(n) && n > 0)
          if (!valid) {
            errors.push(
              `Узел ${node.id}: maxFires должен быть целым числом > 0 или whole-string {{...}}-шаблоном`,
            )
          }
        }
        ;(node.steps ?? []).forEach((n) => validateNode(n, scopeIds, depth + 1))
        break
      }
    }
  }

  state.steps.forEach((node) => validateNode(node, allIds, 1))

  return errors
}

/** Сериализует форму в ChainDto (для POST /chains). */
export const toChainDto = (
  state: ChainFormState,
  descriptors: Descriptors,
): Userbot.ChainDto => {
  const dto: Userbot.ChainDto = {
    ...state.extraDto,
    ...serializeLaunchTarget(state),
    name: state.name,
    steps: state.steps.map((s) => buildNode(s, descriptors)),
    schemaVersion: 2,
  }

  if (!isBlank(state.comment)) dto.comment = state.comment
  if (!isBlank(state.scheduleDate)) dto.scheduleDate = state.scheduleDate

  const ctx = parseJsonValue<Record<string, unknown>>(state.contextJson)
  if (ctx) dto.context = ctx

  return dto
}

/** Сериализует форму в шаблон цепочки (без исполнителя и даты). */
export const toChainTemplate = (
  state: ChainFormState,
  descriptors: Descriptors,
): Userbot.ChainTemplate => {
  const template: Userbot.ChainTemplate = {
    ...state.extraDto,
    name: state.name,
    steps: state.steps.map((s) => buildNode(s, descriptors)),
    schemaVersion: 2,
  }
  if (!isBlank(state.comment)) template.comment = state.comment
  const ctx = parseJsonValue<Record<string, unknown>>(state.contextJson)
  if (ctx) template.context = ctx
  return template
}

/* ----------------------- Десериализация DTO → form ----------------------- */

/** Восстанавливает ActionNodeForm из ChainNode (action). */
const actionFromDto = (
  node: Userbot.ActionNode,
  descriptors: Descriptors,
): ActionNodeForm => {
  const descriptor = hasTypedParams(node.action, descriptors)
  let paramsValues: Record<string, unknown> = {}
  const params = node.params ?? {}
  let rawParamsJson = ""

  if (descriptor) {
    paramsValues = paramsToValues(descriptor.params, params)
  }
  rawParamsJson = Object.keys(params).length
    ? JSON.stringify(params, null, 2)
    : ""

  return {
    extraDto: extraNodeFields(node),
    key: nextKey(),
    kind: "action",
    id: node.id,
    action: node.action,
    paramsValues,
    rawParamsJson,
    onError: node.onError ?? "fail",
    retryAttempts: node.retry?.attempts ? String(node.retry.attempts) : "",
    retryBackoffMs: node.retry?.backoffMs ? String(node.retry.backoffMs) : "",
    timeoutMs: node.timeoutMs ? String(node.timeoutMs) : "",
    saves: (node.save ?? []).map((s) => ({
      action: s.action,
      paramsJson: JSON.stringify(s.params ?? {}, null, 2),
    })),
  }
}

/** Базовые поля из любого ChainNode. */
const baseFieldsFromDto = (node: Userbot.BaseNode) => ({
  extraDto: extraNodeFields(node),
  id: node.id,
  onError: node.onError ?? "fail",
  retryAttempts: node.retry?.attempts ? String(node.retry.attempts) : "",
  retryBackoffMs: node.retry?.backoffMs ? String(node.retry.backoffMs) : "",
  timeoutMs: node.timeoutMs ? String(node.timeoutMs) : "",
  saves: (node.save ?? []).map((s) => ({
    action: s.action,
    paramsJson: JSON.stringify(s.params ?? {}, null, 2),
  })),
})

/** Рекурсивно восстанавливает NodeFormState из ChainNode. */
const nodeFromDto = (
  node: Userbot.ChainNode,
  descriptors: Descriptors,
): NodeFormState => {
  switch (node.kind) {
    case "action":
      return actionFromDto(node, descriptors)

    case "branch": {
      const base = baseFieldsFromDto(node)
      return {
        key: nextKey(),
        kind: "branch",
        ...base,
        cases: node.cases.map((c) => ({
          extraDto: Object.fromEntries(
            Object.entries(c).filter(
              ([key]) => !["when", "then"].includes(key),
            ),
          ),
          key: nextKey(),
          when: c.when,
          then: c.then.map((n) => nodeFromDto(n, descriptors)),
        })),
        elseSteps: node.else?.map((n) => nodeFromDto(n, descriptors)),
      }
    }

    case "forEach": {
      const base = baseFieldsFromDto(node)
      return {
        key: nextKey(),
        kind: "forEach",
        ...base,
        items: node.items,
        itemVar: node.itemVar,
        maxIterations: node.maxIterations ? String(node.maxIterations) : "",
        body: node.body.map((n) => nodeFromDto(n, descriptors)),
      }
    }

    case "callChain": {
      const base = baseFieldsFromDto(node)
      return {
        key: nextKey(),
        kind: "callChain",
        ...base,
        templateId: node.templateId,
        clientId: node.clientId ?? "",
        name: node.name ?? "",
        inlineSteps: node.inline?.steps.map((n) => nodeFromDto(n, descriptors)),
        inlineExtraDto: Object.fromEntries(
          Object.entries(node.inline ?? {}).filter(([key]) => key !== "steps"),
        ),
        inputJson: node.input ? JSON.stringify(node.input, null, 2) : "",
      }
    }

    case "wait": {
      const base = baseFieldsFromDto(node)
      return {
        key: nextKey(),
        kind: "wait",
        ...base,
        durationMs: node.durationMs != null ? String(node.durationMs) : "",
        until: node.until ?? "",
      }
    }

    case "flow.onUpdate": {
      const base = baseFieldsFromDto(node)
      return {
        key: nextKey(),
        kind: "flow.onUpdate",
        ...base,
        match: node.match ?? null,
        maxFires: node.maxFires != null ? String(node.maxFires) : "",
        steps: (node[FLOW_ON_UPDATE_BODY_KEY] ?? []).map((n) =>
          nodeFromDto(n, descriptors),
        ),
      }
    }
  }
}

/** Восстанавливает состояние формы из ChainDto/шаблона (для редактирования). */
export const chainStateFromDto = (
  dto: Partial<Userbot.ChainDto>,
  descriptors: Descriptors,
): ChainFormState => {
  const steps: NodeFormState[] = (dto.steps ?? []).map((node) =>
    nodeFromDto(node, descriptors),
  )

  return {
    extraDto: Object.fromEntries(
      Object.entries(dto).filter(
        ([key]) =>
          ![
            "name",
            "comment",
            "context",
            "scheduleDate",
            "steps",
            "schemaVersion",
            "clientId",
            "role",
            "roleMode",
            "targetUnit",
            "targetFilter",
            "targetFilterParams",
          ].includes(key),
      ),
    ),
    ...launchTargetFromDto(dto),
    name: dto.name ?? "",
    comment: dto.comment ?? "",
    contextJson: dto.context ? JSON.stringify(dto.context, null, 2) : "",
    scheduleDate: dto.scheduleDate ?? "",
    steps: steps.length ? steps : [],
  }
}

const extraNodeFields = (node: Userbot.BaseNode): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(node).filter(
      ([key]) =>
        ![
          "id",
          "kind",
          "retry",
          "timeoutMs",
          "save",
          "action",
          "params",
          "cases",
          "else",
          "items",
          "itemVar",
          "maxIterations",
          "body",
          "templateId",
          "inline",
          "input",
          "clientId",
          "name",
          "durationMs",
          "until",
          "match",
          "maxFires",
          "steps",
        ].includes(key),
    ),
  )
